import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, MessageCircle, Share2, Bookmark, MoreHorizontal, User, Trash2, Flag, Sparkles } from 'lucide-react';
import { doc, setDoc, deleteDoc, updateDoc, increment, addDoc, collection } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { toast } from 'sonner';
import { usePopup } from '../../context/PopupContext';

const formatRelativeTime = (timestamp) => {
  if (!timestamp) return 'Just now';
  let date;
  if (timestamp.toDate) date = timestamp.toDate();
  else if (timestamp.seconds) date = new Date(timestamp.seconds * 1000);
  else date = new Date(timestamp);
  
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return 'Just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
};

export const PostCard = ({ 
  post, 
  userProfile, 
  isLiked, 
  isSaved,
  onLikeUpdate,
  onSaveUpdate,
  onDelete
}) => {
  const navigate = useNavigate();
  const { showConfirm } = usePopup();
  const currentUser = auth.currentUser;
  
  const [showOptions, setShowOptions] = useState(false);
  const [isLiking, setIsLiking] = useState(false);
  
  const author = userProfile || { name: 'Anonymous', username: 'anonymous', photoURL: null };
  const isOwnPost = currentUser?.uid === post.uid;

  const handleLike = async (e) => {
    e.stopPropagation();
    if (!currentUser || isLiking) return;
    
    setIsLiking(true);
    const likeRef = doc(db, 'users', currentUser.uid, 'likedPosts', post.id);
    const postRef = doc(db, 'sparkposts', post.id);
    
    try {
      if (isLiked) {
        onLikeUpdate(post.id, false);
        await setDoc(likeRef, { isActive: false }, { merge: true });
        await updateDoc(postRef, { likes: increment(-1) });
      } else {
        onLikeUpdate(post.id, true);
        await setDoc(likeRef, { likedAt: new Date(), isActive: true }, { merge: true });
        await updateDoc(postRef, { likes: increment(1) });
        
        if (currentUser.uid !== post.uid) {
          try {
            await addDoc(collection(db, 'users', post.uid, 'notifications'), {
              type: 'post_like',
              fromUserId: currentUser.uid,
              toUserId: post.uid,
              postId: post.id,
              createdAt: new Date(),
              read: false
            });
          } catch (e) {
            console.error("Failed to send like notification", e);
          }
        }
      }
    } catch (err) {
      console.error("Like failed", err);
      // Revert optimistic update
      onLikeUpdate(post.id, isLiked);
    } finally {
      setIsLiking(false);
    }
  };

  const handleSave = async (e) => {
    e.stopPropagation();
    if (!currentUser) return;
    
    const saveRef = doc(db, 'users', currentUser.uid, 'savedPosts', post.id);
    
    try {
      if (isSaved) {
        onSaveUpdate(post.id, false);
        await deleteDoc(saveRef);
      } else {
        onSaveUpdate(post.id, true);
        await setDoc(saveRef, { savedAt: new Date() });
      }
    } catch (err) {
      console.error("Save failed", err);
      onSaveUpdate(post.id, isSaved);
    }
  };

  const handleShare = async (e) => {
    e.stopPropagation();
    const url = `${window.location.origin}/post/${post.id}`;
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Check out this post on Spark!',
          text: `"${post.caption}"`,
          url: url,
        });
        // Optional: increment share count in DB
      } catch (err) { }
    } else {
      navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard!');
    }
  };
  
  const handleDelete = async (e) => {
    e.stopPropagation();
    showConfirm({
      title: "Delete Post",
      message: "Are you sure you want to delete this post? This action cannot be undone.",
      isDestructive: true,
      confirmText: "Delete",
      onConfirm: async () => {
        try {
          await deleteDoc(doc(db, 'sparkposts', post.id));
          if (onDelete) onDelete(post.id);
          toast.success("Post deleted");
        } catch (err) {
          toast.error("Failed to delete post");
        }
      }
    });
    setShowOptions(false);
  };

  const navigateToPost = () => navigate(`/post/${post.id}`);
  const navigateToProfile = (e) => {
    e.stopPropagation();
    navigate(`/profile/${post.uid}`);
  };

  return (
    <div 
      onClick={navigateToPost}
      className="flex flex-col p-4 border-b border-gray-800 hover:bg-white/5 transition-colors cursor-pointer"
    >
      <div className="flex gap-3">
        {/* Avatar */}
        <div 
          className="w-10 h-10 rounded-full bg-gray-800 shrink-0 overflow-hidden border border-gray-700 cursor-pointer"
          onClick={navigateToProfile}
        >
          {author.photoURL ? (
            <img src={author.photoURL} alt="Avatar" className="w-full h-full object-cover" />
          ) : (
            <User className="w-full h-full p-2 text-gray-400" />
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5 truncate">
              <span 
                className="font-bold text-white hover:underline truncate cursor-pointer"
                onClick={navigateToProfile}
              >
                {author.name || 'Anonymous'}
              </span>
              {author.isPremium && (
                <Sparkles size={14} className="text-[#0BD9E5] shrink-0" />
              )}
              <span className="text-gray-500 text-[15px] truncate">
                @{author.username || 'user'}
              </span>
              <span className="text-gray-500 text-[15px]">·</span>
              <span className="text-gray-500 text-[15px] whitespace-nowrap">
                {formatRelativeTime(post.createdAt)}
              </span>
            </div>
            
            <div className="relative">
              <button 
                className="text-gray-500 hover:text-[#0BD9E5] p-1.5 rounded-full hover:bg-[#0BD9E5]/10 transition-colors"
                onClick={(e) => { e.stopPropagation(); setShowOptions(!showOptions); }}
              >
                <MoreHorizontal size={18} />
              </button>
              
              {showOptions && (
                <div className="absolute right-0 top-full mt-1 bg-black border border-gray-800 rounded-xl shadow-lg shadow-black/50 py-1 z-10 w-48 overflow-hidden">
                  {isOwnPost ? (
                    <button 
                      onClick={handleDelete}
                      className="w-full px-4 py-3 text-left text-[#ff4444] hover:bg-white/5 flex items-center gap-3 font-bold text-[15px]"
                    >
                      <Trash2 size={18} /> Delete Post
                    </button>
                  ) : (
                    <button 
                      onClick={(e) => { e.stopPropagation(); toast.error("Reported!"); setShowOptions(false); }}
                      className="w-full px-4 py-3 text-left text-white hover:bg-white/5 flex items-center gap-3 font-bold text-[15px]"
                    >
                      <Flag size={18} /> Report Post
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Caption */}
          <p className="text-[15px] text-white whitespace-pre-wrap break-words mt-1">
            {post.caption}
          </p>

          {/* Media */}
          {post.imageUrl && (
            <div className="mt-3 rounded-2xl overflow-hidden border border-gray-800 bg-gray-900">
              <img 
                src={post.imageUrl} 
                alt="Post Media" 
                className="w-full h-auto max-h-[500px] object-cover"
                loading="lazy"
              />
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-between mt-3 max-w-md text-gray-500">
            <button className="flex items-center gap-2 group hover:text-[#0BD9E5] transition-colors">
              <div className="p-2 rounded-full group-hover:bg-[#0BD9E5]/10 transition-colors">
                <MessageCircle size={18} />
              </div>
              <span className="text-[13px]">{post.commentCount || 0}</span>
            </button>

            <button 
              onClick={handleLike}
              className={`flex items-center gap-2 group transition-colors ${isLiked ? 'text-pink-500' : 'hover:text-pink-500'}`}
            >
              <div className={`p-2 rounded-full transition-colors ${isLiked ? 'bg-pink-500/10' : 'group-hover:bg-pink-500/10'}`}>
                <Heart size={18} fill={isLiked ? "currentColor" : "none"} />
              </div>
              <span className="text-[13px]">{post.likes || 0}</span>
            </button>

            <button 
              onClick={handleSave}
              className={`flex items-center gap-2 group transition-colors ${isSaved ? 'text-yellow-500' : 'hover:text-yellow-500'}`}
            >
              <div className={`p-2 rounded-full transition-colors ${isSaved ? 'bg-yellow-500/10' : 'group-hover:bg-yellow-500/10'}`}>
                <Bookmark size={18} fill={isSaved ? "currentColor" : "none"} />
              </div>
            </button>

            <button 
              onClick={handleShare}
              className="flex items-center gap-2 group hover:text-green-500 transition-colors"
            >
              <div className="p-2 rounded-full group-hover:bg-green-500/10 transition-colors">
                <Share2 size={18} />
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PostCard;
